import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

import CorrectSlide from '../../../assets/correct-slide.png';
import CorrectValve from '../../../assets/correct-valve.png';
import EmptyCorrectValve from '../../../assets/empty-correct-valve.png';
import HelpButtonImage from '../../../assets/help-button.png';
import IncorrectValve from '../../../assets/incorrect-valve.png';
import Logo from '../../../assets/logo.png';
import optionsButtonImage from '../../../assets/options-button.png';
import BackButton from '../../../components/BackButton/BackButton';
import Instrument from '../../../components/Instrument/Instrument';
import Loading from '../../../components/Loading/Loading';
import Options from '../../../components/Options/Options';
import ScaleNotes from '../../../components/ScaleNotes/ScaleNotes';
import Stopwatch from '../../../components/Stopwatch/Stopwatch';
import { brassDict } from '../../../utils/Dictionaries/brassDict';
import { scalesDict } from '../../../utils/Dictionaries/scalesDict';
import Styles from './Scales.module.css';

const Scales = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [resetStopwatch, setResetStopwatch] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [incorrect, setIncorrect] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [showOptions, setShowOptions] = useState(false);

  const totalAnsweredRef = useRef(-1);

  const navigate = useNavigate();

  const [stats, setStats] = useState({
    E1: [0, 0],
    'E#1': [0, 0],
    Fb1: [0, 0],
    F1: [0, 0],
    'F#1': [0, 0],
    Gb1: [0, 0],
    G1: [0, 0],
    'G#1': [0, 0],
    Ab1: [0, 0],
    A1: [0, 0],
    'A#1': [0, 0],
    Bb1: [0, 0],
    B1: [0, 0],
    'B#1': [0, 0],
    Cb2: [0, 0],
    C2: [0, 0],
    'C#2': [0, 0],
    Db2: [0, 0],
    D2: [0, 0],
    'D#2': [0, 0],
    Eb2: [0, 0],
    E2: [0, 0],
    'E#2': [0, 0],
    Fb2: [0, 0],
    F2: [0, 0],
    'F#2': [0, 0],
    Gb2: [0, 0],
    G2: [0, 0],
    'G#2': [0, 0],
    Ab2: [0, 0],
    A2: [0, 0],
    'A#2': [0, 0],
    Bb2: [0, 0],
    B2: [0, 0],
    'B#2': [0, 0],
    Cb3: [0, 0],
    C3: [0, 0],
    'C#3': [0, 0],
    Db3: [0, 0],
    D3: [0, 0],
    'D#3': [0, 0],
    Eb3: [0, 0],
    E3: [0, 0],
    'E#3': [0, 0],
    Fb3: [0, 0],
    F3: [0, 0],
    'F#3': [0, 0],
    Gb3: [0, 0],
    G3: [0, 0],
    'G#3': [0, 0],
    Ab3: [0, 0],
    A3: [0, 0],
    'A#3': [0, 0],
    Bb3: [0, 0],
    B3: [0, 0],
    'B#3': [0, 0],
    Cb4: [0, 0],
    C4: [0, 0],
    'C#4': [0, 0],
    Db4: [0, 0],
    D4: [0, 0],
    'D#4': [0, 0],
    Eb4: [0, 0],
    E4: [0, 0],
    'E#4': [0, 0],
    Fb4: [0, 0],
    F4: [0, 0],
    'F#4': [0, 0],
    Gb4: [0, 0],
    G4: [0, 0],
    'G#4': [0, 0],
    Ab4: [0, 0],
    A4: [0, 0],
    'A#4': [0, 0],
    Bb4: [0, 0],
    B4: [0, 0],
    'B#4': [0, 0],
    Cb5: [0, 0],
    C5: [0, 0],
    'C#5': [0, 0],
    Db5: [0, 0],
    D5: [0, 0],
    'D#5': [0, 0],
    Eb5: [0, 0],
    E5: [0, 0],
    'E#5': [0, 0],
    Fb5: [0, 0],
    F5: [0, 0],
    'F#5': [0, 0],
    Gb5: [0, 0],
    G5: [0, 0],
    'G#5': [0, 0],
    Ab5: [0, 0],
    A5: [0, 0],
    'A#5': [0, 0],
    Bb5: [0, 0],
    B5: [0, 0],
    'B#5': [0, 0],
    Cb6: [0, 0],
    C6: [0, 0],
  });

  // Load options from storage
  const loadOptions = () => {
    const storedData = JSON.parse(localStorage.getItem('options'));
    if (storedData) {
      return storedData;
    }
    return {
      selectedInstrument: 'trumpet',
      volume: 50,
      scaleKey: 'C',
      scaleOctave: 4,
    };
  };

  const [selectedInstrument, setSelectedInstrument] = useState(
    loadOptions().selectedInstrument,
  );
  const [volume, setVolume] = useState(loadOptions().volume);
  const [scaleKey, setScaleKey] = useState(loadOptions().scaleKey || 'C');
  const [scaleOctave, setScaleOctave] = useState(
    loadOptions().scaleOctave || 4,
  );

  // Stopwatch
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [fastestTime, setFastestTime] = useState(99999999);

  const handleElapsedChange = useCallback((t) => {
    setElapsedTime(t);
  }, []);

  const updateFastestTime = useCallback(() => {
    setFastestTime((prev) => Math.min(elapsedTime, prev));
  }, [elapsedTime]);

  function formatTime(timeInMs) {
    let minutes = Math.floor((timeInMs / (1000 * 60)) % 60);
    let seconds = Math.floor((timeInMs / 1000) % 60);
    let milliseconds = Math.floor((timeInMs % 1000) / 10);

    minutes = String(minutes).padStart(2, '0');
    seconds = String(seconds).padStart(2, '0');
    milliseconds = String(milliseconds).padStart(2, '0');

    return `${minutes}:${seconds}:${milliseconds}`;
  }

  useEffect(() => {
    setElapsedTime(0);
    setIsTimerRunning(false);
  }, [resetStopwatch]);

  let noteIndexRef = useRef(0);
  const [displayIndex, setDisplayIndex] = useState(0);

  // Build current scale identifier with fallback
  const getCurrentScale = () => {
    const scaleId = `${scaleKey}${scaleOctave}`;
    const scale = scalesDict[selectedInstrument]?.[scaleId];

    if (scale && scale.length > 0) {
      return scale;
    }

    // Fallback to first available scale for this instrument
    const firstScale = Object.values(scalesDict[selectedInstrument] || {})[0];
    return firstScale || [];
  };

  const currentScale = getCurrentScale();

  const changeNote = () => {
    noteIndexRef.current = noteIndexRef.current + 1;
    setDisplayIndex(noteIndexRef.current);
  };

  const reset = () => {
    const opts = loadOptions();
    setSelectedInstrument(opts.selectedInstrument);
    setScaleKey(opts.scaleKey || 'C');
    setScaleOctave(opts.scaleOctave || 4);
    setVolume(opts.volume);
    setResetStopwatch((prev) => prev + 1);
    noteIndexRef.current = 0;
    setDisplayIndex(0);
    setCorrect(0);
    setIncorrect(0);
    setIsTimerRunning(false);
    setElapsedTime(0);
  };

  const restartSequence = ({
    resetStopwatch = true,
    resetCounters = false,
  } = {}) => {
    if (resetStopwatch) setResetStopwatch((prev) => prev + 1);
    noteIndexRef.current = 0;
    setDisplayIndex(0);
    setIsTimerRunning(false);
    setElapsedTime(0);
    if (resetCounters) {
      setCorrect(0);
      setIncorrect(0);
    }
  };

  useEffect(() => {
    reset();
  }, []);

  const handleGraphData = async (e) => {
    const url = 'https://brassflows.pythonanywhere.com/api/results';
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ stats }),
    };

    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      return data.graph;
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }
  };

  const finish = async () => {
    if (correct + incorrect === 0) return;

    const graph = await handleGraphData();

    const formattedTime = formatTime(fastestTime);

    const resultsData = {
      formattedTime,
      notes: correct * 15 + incorrect,
      accuracy,
      graph,
      correct: correct,
    };
    localStorage.setItem('localResults', JSON.stringify(resultsData));

    navigate('/classroom/scales/results');
  };

  const toggleOptions = () => {
    setShowOptions((prev) => {
      const newVal = !prev;
      if (prev === true && newVal === false) {
        // Only reload options and reset the sequence, not the statistics
        const opts = loadOptions();
        setSelectedInstrument(opts.selectedInstrument);
        setScaleKey(opts.scaleKey || 'C');
        setScaleOctave(opts.scaleOctave || 4);
        setVolume(opts.volume);
        noteIndexRef.current = 0;
        setDisplayIndex(0);
        setResetStopwatch((prev) => prev + 1);
        setIsTimerRunning(false);
        setElapsedTime(0);
      }
      return newVal;
    });
  };

  useEffect(() => {
    setAccuracy(((correct / (correct + incorrect)) * 100).toFixed(1));
  }, [correct, incorrect]);

  const [showCorrectValve1, setShowCorrectValve1] = useState(null);
  const [showCorrectValve2, setShowCorrectValve2] = useState(null);
  const [showCorrectValve3, setShowCorrectValve3] = useState(null);
  const [showCorrectValve4, setShowCorrectValve4] = useState(null);
  const [showCorrectTrigger, setShowCorrectTrigger] = useState(null);
  const [showCorrectSlide, setShowCorrectSlide] = useState(null);
  const [correctSlideDistance, setCorrectSlideDistance] = useState(0);
  const [correctSlideNumber, setCorrectSlideNumber] = useState(1);

  const [feedbackType, setFeedbackType] = useState(null);
  const feedbackTimerRef = useRef(null);

  const showFeedback = (type, duration = 250) => {
    setFeedbackType(type);
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
    }
    feedbackTimerRef.current = setTimeout(() => {
      setFeedbackType(null);
      feedbackTimerRef.current = null;
    }, duration);
  };

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  const handleIncorrect = (showCorrectAnswer, pressedKeys = []) => {
    const combos =
      brassDict[selectedInstrument]?.[currentScale[displayIndex]] || [];
    let correctKeys = [];
    if (combos.length > 0) {
      const first = combos[0];
      if (typeof first === 'string') {
        correctKeys = first.split('');
      } else if (Array.isArray(first)) {
        correctKeys = first.map((k) => String(k));
      } else {
        correctKeys = [];
      }
    }

    if (showCorrectAnswer) {
      showFeedback('incorrect', 2000);
    }

    if (['trumpet', 'euphonium', 'tuba'].includes(selectedInstrument)) {
      if (showCorrectAnswer) {
        setShowCorrectValve1(correctKeys.includes('j') ? 1 : 0);
        setShowCorrectValve2(correctKeys.includes('k') ? 1 : 0);
        setShowCorrectValve3(correctKeys.includes('l') ? 1 : 0);
        setShowCorrectValve4(correctKeys.includes(';') ? 1 : 0);
      } else {
        setShowCorrectValve1(null);
        setShowCorrectValve2(null);
        setShowCorrectValve3(null);
        setShowCorrectValve4(null);
      }
    } else if (selectedInstrument === 'frenchHorn') {
      if (showCorrectAnswer) {
        setShowCorrectValve1(correctKeys.includes('q') ? 1 : 0);
        setShowCorrectValve2(correctKeys.includes('w') ? 1 : 0);
        setShowCorrectValve3(correctKeys.includes('e') ? 1 : 0);
        setShowCorrectTrigger(correctKeys.includes('c') ? 1 : 0);
      } else {
        setShowCorrectValve1(null);
        setShowCorrectValve2(null);
        setShowCorrectValve3(null);
        setShowCorrectTrigger(null);
      }
    } else {
      const slidePositions = {
        1: 736,
        2: 660,
        3: 586,
        4: 513,
        5: 439,
        6: 363,
        7: 290,
      };
      const slideKey = correctKeys.find((k) => k in slidePositions);
      if (slideKey) {
        setCorrectSlideDistance(slidePositions[slideKey]);
      }
      setCorrectSlideNumber(
        Number(
          brassDict[selectedInstrument]?.[currentScale[displayIndex]]?.[0],
        ),
      );
      setShowCorrectSlide(showCorrectAnswer);
    }
    restartSequence({ resetStopwatch: true, resetCounters: false });
  };

  const handleAnswer = async (isCorrect) => {
    const currentNote = currentScale[displayIndex];

    if (isCorrect) {
      if (!isTimerRunning) {
        setElapsedTime(0);
        setIsTimerRunning(true);
      }

      const safeNote = currentNote.replace('#', 's');
      const sound = new Audio(`/sounds/${selectedInstrument}/${safeNote}.mp3`);
      sound.volume = volume / 100;
      sound.load();
      sound.play();

      setStats((prevStats) => ({
        ...prevStats,
        [currentNote]: [
          prevStats[currentNote][0] + 1,
          prevStats[currentNote][1],
        ],
      }));
    } else {
      setStats((prevStats) => ({
        ...prevStats,
        [currentNote]: [
          prevStats[currentNote][0],
          prevStats[currentNote][1] + 1,
        ],
      }));
      setIncorrect((prev) => prev + 1);
      totalAnsweredRef.current++;
    }

    if (!isCorrect) {
      restartSequence({ resetStopwatch: true, resetCounters: false });
      return;
    }

    const scaleLen = currentScale.length;
    if (displayIndex >= scaleLen - 1) {
      showFeedback('correct');
      setIsTimerRunning(false);
      updateFastestTime();
      setCorrect((prev) => prev + 1);
      noteIndexRef.current = 0;
      setDisplayIndex(0);
      return;
    }

    changeNote();
  };

  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      exitOnEsc: true,
    },
  });
  if (selectedInstrument === 'trumpet' || selectedInstrument === 'euphonium') {
    tour.addStep({
      id: 'step-1',
      text: 'Use J, K, and L to press down the valves, and space bar for open fingerings.',
      attachTo: {
        element: '.instrument',
        on: 'bottom',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  } else if (selectedInstrument === 'frenchHorn') {
    tour.addStep({
      id: 'step-1',
      text: 'Use Q, W, E, and C to press down the valves and trigger, and space bar for open fingerings.',
      attachTo: {
        element: '.instrument',
        on: 'top',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  } else if (selectedInstrument === 'tuba') {
    tour.addStep({
      id: 'step-1',
      text: 'Use J, K, L, and ; to press down the valves, and space bar for open fingerings.',
      attachTo: {
        element: '.instrument',
        on: 'bottom',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  } else {
    tour.addStep({
      id: 'step-1',
      text: 'Use the numbers 2-7 to change the slide position, and space bar for first position.',
      attachTo: {
        element: '.instrument',
        on: 'bottom',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  }

  const toggleHelp = () => {
    tour.start();
  };

  const handleLoading = (loading) => {
    setIsLoading(loading);
  };

  return (
    <div>
      {isLoading && <Loading />}
      <div
        className={`${Styles.pageWrapper} ${
          showOptions ? Styles.darkerBackground : ''
        }`}
        style={{
          opacity: isLoading ? 0 : 1,
        }}
      >
        <div className={Styles.subButtonsContainer}>
          <BackButton destination={'classroom'} />
          <button className={Styles.options} onClick={toggleOptions}>
            <img src={optionsButtonImage} alt="Options button" />
          </button>
          <button
            className={Styles.help}
            onClick={toggleHelp}
            onKeyDown={(e) => {
              if (e.key === ' ') {
                e.preventDefault();
              }
            }}
          >
            <img src={HelpButtonImage} alt="Help button" />
          </button>
        </div>
        <div className={Styles.practiceWrapper}>
          <div
            className={`${Styles.feedbackOverlay} ${
              feedbackType
                ? feedbackType === 'correct'
                  ? Styles.correct
                  : Styles.incorrect
                : ''
            } ${feedbackType ? Styles.show : ''}`}
          />
          <h4 className={Styles.noteName}>{currentScale[displayIndex]}</h4>
          <div className={Styles.musicDisplay}>
            <div className={Styles.scaleNotesContainer}>
              <ScaleNotes
                className={Styles.scaleNotes}
                keySignature={currentScale[0]}
                selectedInstrument={selectedInstrument}
                displayIndex={displayIndex}
              />
            </div>
            <div className={`${Styles.instrument} instrument`}>
              <Instrument
                correctKeys={
                  brassDict[selectedInstrument]?.[currentScale[displayIndex]]
                }
                handleIncorrect={handleIncorrect}
                handleAnswer={handleAnswer}
                instrument={selectedInstrument}
                handleLoading={handleLoading}
                mode={'Scales'}
              />
            </div>
          </div>
          <div className={Styles.dashboard}>
            <div className={Styles.logoContainer}>
              <img src={Logo} alt={'Website logo'} />
            </div>
            <Stopwatch
              resetStopwatch={resetStopwatch}
              running={isTimerRunning}
              onElapsedChange={handleElapsedChange}
            />
            <div className={Styles.cardStats}>
              <div className={Styles.individualStat}>
                <h3>correct</h3>
                <h2>{correct}</h2>
              </div>
              <div className={Styles.individualStat}>
                <h3>incorrect</h3>
                <h2>{incorrect}</h2>
              </div>
              <div className={Styles.individualStat}>
                <h3>accuracy</h3>
                <h2>{correct === 0 && incorrect === 0 ? '0.0' : accuracy}%</h2>
              </div>
            </div>
            <div className={Styles.buttonsContainer}>
              <button
                className={Styles.button}
                onClick={reset}
                onKeyDown={(event) => {
                  if (event.key === ' ') {
                    event.preventDefault();
                  }
                }}
              >
                Reset
              </button>
              <button
                className={Styles.button}
                onClick={finish}
                onKeyDown={(event) => {
                  if (event.key === ' ') {
                    event.preventDefault();
                  }
                }}
              >
                Finish
              </button>
            </div>
          </div>
        </div>
        <div>
          {selectedInstrument === 'trombone' ? (
            <div>
              {showCorrectSlide ? (
                <div
                  className={Styles.slide}
                  style={{ marginRight: `${correctSlideDistance}px` }}
                >
                  <img src={CorrectSlide} alt={'Correct slide'} />
                  <div className={Styles.slideNumberContainer}>
                    <p>{correctSlideNumber}</p>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          ) : selectedInstrument === 'tuba' ? (
            <div className={Styles.rotaryValves}>
              {showCorrectValve1 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve1 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve2 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve2 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve3 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve3 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve4 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve4 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
            </div>
          ) : (
            <div
              className={
                selectedInstrument === 'frenchHorn'
                  ? Styles.rotaryValves
                  : Styles.valves
              }
            >
              {showCorrectValve1 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve1 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve2 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve2 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve3 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve3 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectTrigger === 1 ? (
                <img
                  className={Styles.trigger}
                  src={CorrectValve}
                  alt={'Show correct trigger'}
                />
              ) : showCorrectTrigger === 0 ? (
                <img
                  className={Styles.trigger}
                  src={IncorrectValve}
                  alt={'Show incorrect trigger'}
                />
              ) : (
                <img
                  className={Styles.emptyTrigger}
                  src={EmptyCorrectValve}
                  alt={'Empty correct trigger'}
                />
              )}
            </div>
          )}
        </div>
        {showOptions && (
          <Options toggleOptions={toggleOptions} page={'scales'} />
        )}
      </div>
    </div>
  );
};

export default Scales;
